// countryMask.js — "Country Focus Mask": inverse-polygon overlay that
// keeps the active deployment's country in full-color satellite/vector
// imagery while darkening (rgba(0,0,0,0.85)) everything outside it.
// Geographic (not a rasterized image) so it stays pixel-accurate on
// zoom/pan/tilt/resize. Boundary geometry comes from the backend
// (`GET /api/geo/boundary`), itself sourced from Natural Earth 1:10m
// Admin 0 Countries — see /app/backend/country_boundary.py.
//
// Countries without a boundary file on disk (e.g. Guinée, for now)
// simply get `available:false` from the API — callers must skip
// installing the mask in that case, never fabricate a rough shape.

import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MASK_SOURCE_ID = "country-focus-mask-src";
const MASK_LAYER_ID = "country-focus-mask";
const MASK_COLOR = "rgba(0, 0, 0, 0.85)";

let _boundaryPromise = null;

/** Fetches the active deployment's country boundary once and caches the
 *  promise (shared across all 3 maps + every style swap). Resolves to
 *  `null` when no boundary is on file yet — callers must skip the mask. */
export function getCountryBoundary() {
  if (!_boundaryPromise) {
    _boundaryPromise = axios
      .get(`${BACKEND}/api/geo/boundary`, { timeout: 8000 })
      .then(({ data }) => (data && data.available ? data.geojson : null))
      .catch(() => null);
  }
  return _boundaryPromise;
}

// Slightly inset from the poles (not ±90) — avoids the degenerate
// tile-projection seam MapLibre's raster/vector Mercator can render at
// the exact poles, with zero visible impact anywhere a user can pan to.
const WORLD_RING = [
  [-180, -85], [180, -85], [180, 85], [-180, 85], [-180, -85],
];

/** Builds the inverse "world minus country" mask polygon: outer ring is
 *  the whole world, with one hole per polygon in the country's geometry.
 *  MapLibre's fill tessellation (earcut) determines holes from ring
 *  ORDER (first = outer, rest = holes) — absolute winding direction
 *  doesn't matter, so the source rings are used as-is. */
function buildMaskGeoJSON(boundaryFeature) {
  const geom = boundaryFeature.geometry;
  const polygons = geom.type === "Polygon" ? [geom.coordinates] : geom.coordinates;
  const holes = polygons.map((rings) => rings[0]);
  return {
    type: "Feature",
    properties: {},
    geometry: { type: "Polygon", coordinates: [WORLD_RING, ...holes] },
  };
}

/** Installs the Country Focus Mask on `map`. Idempotent within a style
 *  lifecycle (safe to call from both the initial "load" and every
 *  post-`setStyle()` "styledata" reinstall). MUST be called BEFORE the
 *  map's own operational overlays (POIs/heatmap/clusters/GPS/alerts) are
 *  installed in that same cycle, so the mask always renders below them
 *  and above the base style — never call this after those layers exist. */
export function installCountryMask(map, boundaryFeature) {
  if (!map || !boundaryFeature) return;
  if (map.getLayer(MASK_LAYER_ID)) return;
  const data = buildMaskGeoJSON(boundaryFeature);
  if (map.getSource(MASK_SOURCE_ID)) {
    map.getSource(MASK_SOURCE_ID).setData(data);
  } else {
    map.addSource(MASK_SOURCE_ID, { type: "geojson", data });
  }
  if (!map.getLayer(MASK_LAYER_ID)) {
    map.addLayer({
      id: MASK_LAYER_ID,
      type: "fill",
      source: MASK_SOURCE_ID,
      paint: { "fill-color": MASK_COLOR },
    });
  }
}
